import { useState, useCallback, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import type { ContractSegment } from '../../engine/types'
import { CONTRACT_DOCUMENT } from '../../data/contractDocument'
import { getIconifyUrl } from '../../data/cardIcons'
import { useContractGameStore } from '../../store/useContractGameStore'

interface Particle {
  id: number
  x: number
  y: number
  color: 'green' | 'amber'
  dx: number
  dy: number
  size: number
  delay: number
  duration: number
}

let particleIdCounter = 0

interface ContractDocumentPanelProps {
  fullWidth?: boolean
  progressRef?: RefObject<HTMLSpanElement | null>
}

export function ContractDocumentPanel({ fullWidth, progressRef }: ContractDocumentPanelProps) {
  const { t } = useTranslation()
  const phase = useContractGameStore((s) => s.phase)
  const discoveredClauseIds = useContractGameStore((s) => s.discoveredClauseIds)
  const clauseOutcomes = useContractGameStore((s) => s.clauseOutcomes)
  const activeClauseId = useContractGameStore((s) => s.activeClauseId)
  const dispatch = useContractGameStore((s) => s.dispatch)

  const [flashDecoyId, setFlashDecoyId] = useState<string | null>(null)
  const [particles, setParticles] = useState<Particle[]>([])

  const isDiscoveryPhase = phase === 'CONTRACT_DISCOVERY'
  const isGameOver = phase === 'GAME_OVER'

  const spawnParticles = useCallback((e: React.MouseEvent, color: 'green' | 'amber') => {
    const cx = e.clientX
    const cy = e.clientY
    const count = color === 'green' ? 10 : 6

    let baseDx = 0
    let baseDy = 0
    if (color === 'green' && progressRef?.current) {
      const rect = progressRef.current.getBoundingClientRect()
      baseDx = rect.left + rect.width / 2 - cx
      baseDy = rect.top + rect.height / 2 - cy
    }

    const newParticles: Particle[] = Array.from({ length: count }, () => {
      const spread = color === 'green' ? 30 : 40
      const offsetX = (Math.random() - 0.5) * spread
      const offsetY = (Math.random() - 0.5) * spread
      return {
        id: ++particleIdCounter,
        x: cx + offsetX,
        y: cy + offsetY,
        color,
        dx: baseDx - offsetX + (Math.random() - 0.5) * 20,
        dy: baseDy - offsetY + (Math.random() - 0.5) * 20,
        size: 3 + Math.random() * 5,
        delay: Math.random() * 120,
        duration: color === 'green' ? 600 + Math.random() * 400 : 400 + Math.random() * 300,
      }
    })

    setParticles((prev) => [...prev, ...newParticles])

    const maxLife = Math.max(...newParticles.map((p) => p.delay + p.duration)) + 50
    setTimeout(() => {
      const ids = new Set(newParticles.map((p) => p.id))
      setParticles((prev) => prev.filter((p) => !ids.has(p.id)))
    }, maxLife)
  }, [progressRef])

  const handleSegmentClick = useCallback((seg: ContractSegment, e: React.MouseEvent) => {
    if (!isDiscoveryPhase) return
    if (seg.type === 'keyword') {
      dispatch({ type: 'DISCOVER_KEYWORD', clauseId: seg.clauseId })
      spawnParticles(e, 'green')
    } else if (seg.type === 'decoy') {
      dispatch({ type: 'CLICK_DECOY', decoyId: seg.id })
      spawnParticles(e, 'amber')
      setFlashDecoyId(seg.id)
      setTimeout(() => setFlashDecoyId(null), 800)
    }
  }, [isDiscoveryPhase, dispatch, spawnParticles])

  const getOutcomeForClause = (clauseId: string) => {
    return clauseOutcomes.find(o => o.clauseId === clauseId)
  }

  const getOutcomeBorderColor = (result: string): string => {
    switch (result) {
      case 'success': return '#7DAF8B'
      case 'full': return '#C9A96E'
      case 'failure': return '#D4849A'
      default: return 'transparent'
    }
  }

  const renderSegment = (seg: ContractSegment, idx: number) => {
    if (seg.type === 'normal') {
      return <span key={idx}>{seg.text}</span>
    }

    if (seg.type === 'keyword') {
      const isDiscovered = discoveredClauseIds.includes(seg.clauseId)
      const isActive = activeClauseId === seg.clauseId
      const outcome = isGameOver ? getOutcomeForClause(seg.clauseId) : undefined

      const segStyle: React.CSSProperties = {
        cursor: isDiscoveryPhase && !isDiscovered ? 'pointer' : 'default',
        borderBottom: isDiscovered ? '2px solid #D4849A' : 'none',
        paddingBottom: isDiscovered ? 1 : 0,
        position: 'relative',
        transition: 'all 0.2s',
      }

      if (isDiscoveryPhase && !isDiscovered) {
        segStyle.borderBottom = '1px dashed transparent'
      }

      if (isActive && phase === 'CHALLENGE_RESOLUTION') {
        segStyle.background = 'rgba(201, 169, 110, 0.15)'
        segStyle.boxShadow = '0 0 8px rgba(201, 169, 110, 0.2)'
        segStyle.borderRadius = 2
        segStyle.padding = '0 2px'
      }

      if (outcome) {
        segStyle.borderBottom = `3px solid ${getOutcomeBorderColor(outcome.result)}`
        segStyle.paddingBottom = 2
      }

      return (
        <span
          key={idx}
          style={segStyle}
          className={isDiscoveryPhase && !isDiscovered ? 'discovery-hover' : undefined}
          onClick={(e) => handleSegmentClick(seg, e)}
        >
          {seg.text}
          {isDiscovered && !isGameOver && (
            <img src={getIconifyUrl('lucide:check', 12, '#D4849A')} alt="" className="w-3 h-3 inline-block ml-0.5" />
          )}
        </span>
      )
    }

    if (seg.type === 'decoy') {
      const isFlashing = flashDecoyId === seg.id

      const segStyle: React.CSSProperties = {
        cursor: isDiscoveryPhase ? 'pointer' : 'default',
        transition: 'background 0.3s',
        borderRadius: 2,
      }

      if (isDiscoveryPhase) {
        segStyle.borderBottom = '1px dashed transparent'
      }

      if (isFlashing) {
        segStyle.background = 'rgba(201, 169, 110, 0.2)'
      }

      return (
        <span
          key={idx}
          style={segStyle}
          className={isDiscoveryPhase ? 'discovery-hover' : undefined}
          onClick={(e) => handleSegmentClick(seg, e)}
        >
          {seg.text}
        </span>
      )
    }

    return null
  }

  const doc = CONTRACT_DOCUMENT

  return (
    <article className={fullWidth
      ? 'w-full h-full overflow-y-auto'
      : 'flex-1 h-full overflow-y-auto bg-bg-document border-r border-border-warm shrink-0'
    } aria-label={doc.title}>
      <style>{`
        .discovery-hover {
          border-radius: 3px;
          padding: 1px 3px;
          margin: -1px -3px;
        }
        .discovery-hover:hover {
          border-bottom-color: #6B5F88 !important;
          background: rgba(107, 95, 136, 0.12);
          box-shadow: 0 1px 4px rgba(107, 95, 136, 0.12);
        }
      `}</style>
      <div className="max-w-[640px] mx-auto px-4 py-6 md:px-12 md:py-10 min-h-full font-document text-text-document bg-bg-document-inner rounded-lg my-4 mx-4 leading-[1.8] text-[13px] md:text-[14px]">
        {/* Title */}
        <div className="text-center text-[20px] font-bold mb-2 tracking-[2px]">{doc.title}</div>
        <div className="text-center text-[12px] text-text-document-muted mb-6">{t('contract.contractNumber', { number: doc.contractNumber })}</div>

        {/* Parties */}
        <div className="mb-5 text-[13px] leading-[2] border-b border-border-warm pb-4">
          <div>{t('contract.partyA', { name: doc.parties.partyA })}</div>
          <div>{t('contract.partyB', { name: doc.parties.partyB })}</div>
        </div>

        {/* Preamble */}
        <div className="mb-6 indent-[2em] text-[13px] text-text-document-preamble">{doc.preamble}</div>

        {/* Sections */}
        {doc.sections.map((section, sIdx) => (
          <div key={sIdx} className="mb-5">
            <div className="text-[15px] font-bold mb-2.5 text-text-document-heading">{section.heading}</div>
            {section.paragraphs.map((para, pIdx) => (
              <div key={pIdx} className="indent-[2em] mb-2 text-[14px] text-text-document-body">
                {para.segments.map((seg, segIdx) => renderSegment(seg, segIdx))}
              </div>
            ))}
          </div>
        ))}

        {/* Signature */}
        <div className="mt-12 pt-6 border-t border-border-warm">
          <div className="flex justify-between mb-6">
            <div>
              <div className="text-[12px] text-text-document-muted mb-2">{t('contract.partyASign')}</div>
              <div className="text-[13px] font-semibold border-b border-border-document-sig pb-1 min-w-[180px]">{doc.signature.partyA}</div>
            </div>
            <div>
              <div className="text-[12px] text-text-document-muted mb-2">{t('contract.partyBSign')}</div>
              <div className="text-[13px] font-semibold border-b border-border-document-sig pb-1 min-w-[180px]">{doc.signature.partyB}</div>
            </div>
          </div>
          <div className="text-[12px] text-text-document-muted text-right">{t('contract.signDate', { date: doc.signature.date })}</div>
        </div>
      </div>

      {/* Particles */}
      {particles.map((p) => {
        const isGreen = p.color === 'green'
        const half = p.size / 2
        return (
          <div
            key={p.id}
            className="fixed rounded-full pointer-events-none"
            style={{
              left: p.x - half,
              top: p.y - half,
              width: p.size,
              height: p.size,
              zIndex: 9999,
              background: isGreen
                ? 'radial-gradient(circle at 35% 35%, #80ffbb, #00ff88)'
                : 'radial-gradient(circle at 35% 35%, #fcd34d, #f59e0b)',
              boxShadow: isGreen
                ? `0 0 ${p.size * 2}px ${p.size}px rgba(0,255,136,0.5)`
                : `0 0 ${p.size * 2}px ${p.size}px rgba(245,158,11,0.5)`,
              animation: isGreen
                ? `dot-fly ${p.duration}ms cubic-bezier(0.4, 0, 0.2, 1) ${p.delay}ms forwards`
                : `dot-shake ${p.duration}ms ease-out ${p.delay}ms forwards`,
              '--fly-dx': `${p.dx}px`,
              '--fly-dy': `${p.dy}px`,
            } as React.CSSProperties}
          />
        )
      })}
    </article>
  )
}
